//
// # Labels
//
// The GUI elements that look like text on screen,
// and have some additional stuff behind them.
// Often this is the evaluation of an expression,
// or the capacity to inline edit their text.
//
// Labels also include type-specific styling,
// a facet we may eventually want to break out.
//

// Local Imports
import { exhaust } from "CoreUtils";
import { model, text, graphics, Point } from "GenAlphaModel";
import { MousePos } from "../../mousepos";
import { EntityKind } from "../../entity";
import { Entity } from "../../tree";
import * as style from "../../style";
import { DrawTo } from "../../draw";
import { Special } from "../../style";
import type { RedrawRequest } from "../../redraw";

type TextData = text.Text;

// We very commonly screw up the bounding boxes of text elements.
// Debug switch to draw rectangles for them.
// (Note we also have no way to *remove* these, so they tend to look pretty fun.)
const _DRAW_BBOXES = false;

// # Label Base Class
abstract class LabelBase<DataType> extends Entity<DataType> {
  private bbox: model.Bbox = model.Bbox.empty(); // Bounding box, in schematic coordinates
  abstract drawerData: graphics.Text | null;
  readonly table = makeTextStyleTable(this.kind);
  override readonly cache = this.makeCache<style.TextStyle>(this.table);
  get style(): style.TextStyle {
    return this.cache.style;
  }

  get kind(): model.ExprKind | "wire" {
    return "wire";
  }

  // Shared draw method
  // Dispatches to `draw` for the core drawn-element generation
  redraw = (to: DrawTo): unknown => {
    // if (_DRAW_BBOXES) {
    //   to.bbox(this.bbox, null);
    // }
    if (this.drawerData) {
      if (!isLabelVisible(to, this.kind)) return null;
      to.text(this.drawerData, this.style, this);
      this.updateBbox();
      return null;
    }
    return null;
  };

  updateBbox = () => {
    this.bbox = this.calcBbox();
  };

  calcBbox = (): model.Bbox => {
    if (!this.drawerData) return model.Bbox.empty();
    return graphics.Text.bbox(this.drawerData);
  };

  // Boolean indication of whether `mousePos` is inside the instance.
  hitTest = (mousePos: MousePos) =>
    model.Bbox.contains(this.bbox, mousePos.sch);

  // Abort an in-progress instance.
  abort() {}
}

abstract class EntityLabel<DataType> extends LabelBase<DataType> {
  override update = (data: Partial<DataType>): RedrawRequest => {
    this.data = { ...this.data, ...data };
    return this.root.requestRedraw();
  };
}

type TextWithLabelKind = TextData & { labelKind: model.ExprKind | "wire" };

export class TextLabel extends EntityLabel<TextWithLabelKind> {
  readonly entityKind = EntityKind.TextLabel;

  // Getters from `data`
  get loc(): Point {
    return Point.clone(this.data.loc);
  }
  get drawerData(): graphics.Text | null {
    return this.data;
  }
  override get kind(): model.ExprKind | "wire" {
    return this.data.labelKind;
  }
}

export class ExprLabel extends EntityLabel<model.Expr> {
  readonly entityKind = EntityKind.ExprLabel;

  // Getters from `data`
  get drawerData(): graphics.Text | null {
    return model.Expr.textElem(this.data);
  }
  get loc(): Point {
    return Point.clone(this.data.loc);
  }
  override get kind(): model.ExprKind {
    return this.data.kind;
  }
}

export class EvalLabel extends LabelBase<model.Eval> {
  readonly entityKind = EntityKind.EvalLabel;

  get drawerData(): graphics.Text | null {
    return model.Eval.textElem(this.data);
  }
  get loc(): Point {
    return Point.clone(this.data.expr.loc);
  }
  override get kind(): model.ExprKind {
    return this.data.expr.kind;
  }
}

export type Label = TextLabel | ExprLabel | EvalLabel;

export function makeTextStyleTable(
  kind: model.ExprKind | "wire" = model.ExprKind.Invalid
): style.TextStyleTable {
  return {
    //       light light    dark  dark
    //       no    hilited  no    hilited
    family: style.THE_FONT,
    style: "normal",
    weight: 700, // Typical value for "bold"
    size: 16,
    fill: labelFill(kind),
    stroke: Special.None,
  };
}

// Check whether a label should be visible based on schematic display options.
// When no schematic display options are set (null), all labels are visible.
function isLabelVisible(to: DrawTo, kind: model.ExprKind | "wire"): boolean {
  const opts = to.schematicDisplay;
  if (!opts) return true;
  switch (kind) {
    case model.ExprKind.InstName:
      return opts.instNames;
    case model.ExprKind.CellName:
      return opts.cellNames;
    case model.ExprKind.InstParam:
      return opts.instParams;
    case model.ExprKind.PortName:
      return opts.portNames;
    case model.ExprKind.PortDebug:
      return opts.portDebug;
    case "wire":
      return opts.wireLabels;
    case model.ExprKind.Invalid:
      return true; // Always show error labels
    default:
      exhaust(kind);
  }
}

const wireColors: [style.Color, style.Color, style.Color, style.Color] = [
  style.Color.blue,
  style.Color.red,
  style.Color.new(37, 150, 190),
  style.Color.red,
];

function labelFill(
  kind: model.ExprKind | "wire"
): [style.Color, style.Color, style.Color, style.Color] {
  const { grey, red, green, yellow, maroon, orange, black, white } =
    style.Color;
  switch (kind) {
    case "wire":
      return wireColors;

    case model.ExprKind.PortName:
    case model.ExprKind.InstName:
      //       light light    dark  dark
      //       no    hilited  no    hilited
      return [red, maroon, yellow, white];

    case model.ExprKind.InstParam:
      return [orange, red, orange, white];

    case model.ExprKind.CellName:
      return [black, red, green, white];

    // FIXME: these are more or less default values; maybe find some others
    case model.ExprKind.PortDebug:
    case model.ExprKind.Invalid:
      return [black, red, grey, white];

    default:
      exhaust(kind);
  }
}
